import { Image } from 'js/components/common/Image';
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';

import * as hooks from './hooks';
import * as Types from './types';
import { GameViewLive } from '@core/js/components/main/GamesGrid/components/Game/components/GameViewLive';
import { GameViewBasic } from '@core/js/components/main/GamesGrid/components/Game/components/GameViewBasic';

import style from './Game.scss';

export const GameBare: React.FC<Types.IGamesProps> = ({ gridArea, borderRadius, gameCode, ratio, size, width, height }) => {
    const gameRef = useRef<HTMLDivElement>(null);
    const [imageSrc, setImageSrc] = useState<string[]>([]);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

    const game = hooks.useGetGameConfiguration({ gameCode, ratio, size });

    useEffect(() => {
        const timeoutID = setTimeout(() => {
            if (game?.gameImage) {
                setImageSrc(game.gameImage);
            }
        }, 0);

        return () => {
            clearTimeout(timeoutID);
        };
    }, [game]);

    const onImageLoaded = useCallback(() => {
        setIsImageLoaded(true);
    }, [setIsImageLoaded]);

    const handleGameClick = useCallback((event) => {
        !event?.target?.classList?.contains('cms-games-grid-game-favorite-icon') && setIsFocused(true); // Lugano changes
    }, [setIsFocused]);

    const handleGameBlur = useCallback((event: Event): void => {
        !gameRef.current?.contains(event.target as HTMLElement) && setIsFocused(false);
    }, [setIsFocused]);

    useEffect(() => {
        window.addEventListener('click', handleGameBlur);

        return () => {
            window.removeEventListener('click', handleGameBlur);
        };
    }, [handleGameBlur]);

    const isTableClosed = Boolean(game?.liveConfiguration?.isScheduled);
    const isLiveImageEnabled = game?.isLiveGame ? game?.isLiveImageEnabled : false; // Lugano changes

    return (
        <div
            ref={gameRef}
            onClick={handleGameClick}
            style={{ gridArea, borderRadius, ...width && { width }, ...height && { height } }}
            className={`${style.gameTile} ${size} ${gameCode} ${isFocused ? 'focused' : ''} cms-games-grid-game`}
        >
            <div
                data-ratio={ratio}
                data-game-code={gameCode}
                className={style.container}
            >
                <div className={`${style.defaultImage} ${isTableClosed ? style.grayImage : ''} cms-games-grid-game-default-image`} />

                {Boolean(imageSrc.length) && (
                    <Image
                        src={imageSrc}
                        isRemoveOnError
                        alt={game?.name || ''}
                        onLoad={onImageLoaded}
                        className={[
                            style.gameImage,
                            isTableClosed ? style.grayImage : '',
                            isImageLoaded ? style.showAnimation : '',
                            isLiveImageEnabled ? style.liveImage : '' // Lugano changes
                        ].filter(Boolean).join(' ')}
                    />)
                }
                {game && game.isLiveGame && <GameViewLive game={game} />}
                {game && !game.isLiveGame && <GameViewBasic game={game} />}
            </div>
        </div>
    );
};

export const Game = memo(GameBare);
